<template>
    <app-page>
	    <content-container class="p-1">
		    <card
                :title="user ? user.firstName + ' ' + user.lastName : 'Manage user'"
                :subtitle="group ? group.reportName : user ? user.emailAddress : null"
            >

			    <toolbar class="p-1">
				    <toolbar-group>
					    <toolbar-button title="Go back" @click.prevent="goBack">
						    <icon src="keyboard_backspace" />
					    </toolbar-button>
				    </toolbar-group>

                    <toolbar-group>
                        <toolbar-button 
                        title="Add to group(s)" 
                        icon="group_add" 
                        @click="addGroupUser()"
                        />
                    </toolbar-group>

				    <toolbar-group v-if="singleGroupMode && user && group">
					    <toolbar-button
                            icon="remove_circle_outline"
                            :disabled="isReadOnlyInAnyGroup"
                            @click="removeGroupUser(group)"
                        >
						    Remove from "{{group.reportName}}"
					    </toolbar-button>
                        <tooltip v-if="isReadOnlyInAnyGroup">{{user.firstName}}'s membership in "{{group.reportName}}" is managed by Support Center</tooltip>
				    </toolbar-group>

                    <toolbar-group v-if="allGroupsMode && user && !isMe">
                        <toolbar-button
                            :title="`Delete ${user.firstName}`"
                            :disabled="!canDeleteUser || isReadOnlyInAnyGroup"
                            @click="deleteUser"
                        >
						    <icon src="delete" />
					    </toolbar-button>
                        <tooltip v-if="isReadOnlyInAnyGroup">Users cannot be deleted while they belong to one or more groups managed by Support Center.</tooltip>
                        <tooltip v-else-if="!canDeleteUser" :title="`To delete ${user.firstName}, you must be a Group Admin in every group ${user.firstName} belongs to.`" />
				    </toolbar-group>
			    </toolbar>

			    <section class="px-1 pb-1">
				    <form v-if="user">						
					    <form-section label-width="100">
						    <control-group title="Groups">
                                <collapsible-group :multiple="true" class="mb-0g5">

                                    <collapsible
                                        v-for="(group, index) in editableGroups"
                                        :key="group.locationGroupID"
                                        :active="group.role != 2 || index == 0"
                                        class="user-group-editor"
                                    >
                                        <div class="user-group-editor-header">
                                                
                                            <div v-collapsible-toggle class="user-group-editor-title">
                                                <letter-avatar :name="group.reportName" :size="24" />
                                                <div class="title">{{group.reportName}}</div>
                                            </div>

                                            <div class="user-group-editor-role">
                                                <div v-if="group.readOnly" class="user-group-role-display">
                                                    <icon src="lock" />
                                                    <span class="text">{{getLocationGroupRoleType(group.role)}}</span>
                                                    <tooltip>{{user.firstName}}'s role in "{{group.reportName}}" is managed by Support Center</tooltip>
                                                </div>

                                                <dropdown v-else :overlay="true" align="end">
                                                    <div class="editable-toggle" slot="toggle">
                                                        <div class="editable-toggle-text">{{getLocationGroupRoleType(group.role)}}</div>
                                                        <div class="editable-toggle-icon"><icon src="arrow_drop_down" /></div>
                                                        <tooltip>Edit {{user.firstName}}'s role in "{{group.reportName}}"</tooltip>
                                                    </div>

							                        <list v-if="!isMe">
                                                        <list-item
                                                            v-for="item in roles"
                                                            :key="item.locationGroupRoleTypeID"
									                        :primary-icon="group.role == item.locationGroupRoleTypeID ? 'check' : null" 
									                        :title="item.locationGroupRoleTypeText" 
									                        clickable
                                                            inset
                                                            @click="updateGroup(group, item);"
								                        />
                                                        <list-divider inset />
                                                        <list-item
									                        :title="`Remove ${user.firstName} from &quot;${group.reportName}&quot;`"
									                        clickable
                                                            inset
                                                            @click="removeGroupUser(group)"
                                                        />
							                        </list>

                                                    <list v-else>
                                                        <list-item
									                        title="Revoke group admin access" 
									                        clickable
                                                            @click="revokeGroupAdminAccess(group)"
                                                        />
                                                        <list-item
									                        :title="`Leave &quot;${group.reportName}&quot;`" 
									                        clickable
                                                            @click="removeGroupUser(group)"
                                                        />
							                        </list>
						                        </dropdown>
                                            </div>

                                        </div>
                              
                                        <collapsible-content :collapsed="(group.role == 2 && index != 0)">
                                            <section class="user-group-editor-body pr-2">
                                                <list title="Dashboard access">

                                                    <section v-if="group.role == 2" class="pl-1 pb-0g5">
                                                        As a Group Admin, {{user.firstName}} has "Dashboard Admin" permissions for all dashboards in the "{{group.reportName}}" group.
                                                    </section>

                                                    <list-item
                                                        v-else
                                                        v-for="permission in getPermissionsByGroup(group.locationGroupID)"
                                                        :key="permission.dashboardID"
                                                        :title="permission.dashboardName"
                                                    >
                                                        <icon slot="primary-object" src="dashboard" class="text-dark-secondary" />

                                                        <div slot="secondary-object" class="dashboard-permission-editor">
                                                            <icon src="lock" v-if="permission.readOnly" />
                                                            <select
                                                                v-control.small
                                                                v-model="permission.dashboardPermission"
                                                                :disabled="permission.readOnly"
                                                                @change="updatePermission(permission)"
                                                            >
                                                                <option
                                                                    v-for="item in dashboardPermissionTypes"
                                                                    :key="item.dashboardPermissionTypeID"
                                                                    :value="item.dashboardPermissionTypeID"
                                                                >
                                                                    {{item.dashboardPermissionTypeText}}
                                                                    <template v-if="item.dashboardPermissionTypeID == null"> ({{getDashboardPermissionTypeText(permission.defaultDashboardPermission)}})</template>
                                                                </option>
                                                            </select>
                                                            <tooltip v-if="permission.readOnly">{{user.firstName}}'s dashboard permissions for "{{permission.dashboardName}}" are managed by Support Center</tooltip>
                                                        </div>
                                                    </list-item>

                                                </list>
                                            </section>
                                        </collapsible-content>
                                    </collapsible>

                                </collapsible-group>

							</control-group>
					    </form-section>					
				    </form>
			    </section>

		    </card>


		    <delete-user-modal
			    v-if="user && !isMe"
			    ref="deleteUserModal"
			    :user="user"
			    @delete="confirmDeleteUser"
		    />

            <add-group-user-modal 
                v-if="user && !isMe"
			    ref="addGroupUserModal"
			    :user="user"
                :groups="groups"
                @add="refreshUserGroups"
            />

            <remove-group-user-modal
			    v-if="user && selectedGroupForRemoval"
			    ref="removeGroupUserModal"
			    :user="user"
                :group="selectedGroupForRemoval"
			    @remove="confirmRemoveGroupUser"
		    />

            <revoke-group-admin-access-modal
			    v-if="isMe && selectedGroupForRemoval"
			    ref="revokeGroupAdminAccessModal"
                :group="selectedGroupForRemoval"
			    @revoke="confirmRevokeGroupAdminAccess"
		    />

	    </content-container>
    </app-page>
</template>


<script>
	import _ from 'lodash';
	import { AsyncMixin } from 'pdk/mixins';
	import { notifications } from 'pdk/services';
    import { groups, roles, users } from '@/services';
    import { actions, getters } from '@/store';
	import { DeleteUserModal, AddGroupUserModal, RemoveGroupUserModal, RevokeGroupAdminAccessModal } from './modals';
    import { actions as dashboardStoreActions } from 'dashboard-pdk/store';
    import { dashboards } from 'dashboard-pdk/services';
    import { http } from '@/services';


	export default {
		name: 'manage-user',
		components: {
            DeleteUserModal,
            AddGroupUserModal,
            RemoveGroupUserModal,
            RevokeGroupAdminAccessModal
		},
        mixins: [AsyncMixin],
        beforeRouteEnter(to, __, next) {
            const requests = [
                users.getUser({
                    globalUserID: to.params.globalUserId
                }),
                users.getDashboardPermissions({
                    globalUserID: to.params.globalUserId,
                    locationGroupID: to.params.locationGroupId
                }),
                users.getLocationGroups({
                    globalUserID: to.params.globalUserId,
                    locationGroupID: to.params.locationGroupId
                }),
                roles.getRoles(),
                dashboards.getDashboardPermissionTypes({
                    includeDefault: true
                })
            ];


            http.all(requests)
                .then(http.spread((user, permissions, locationGroups, rolesList, permissionTypes) => {
					next((vm) => {
						vm.user = user;
						vm.permissions = permissions;
                        vm.groups = locationGroups;
                        vm.roles = rolesList;
                        vm.dashboardPermissionTypes = permissionTypes;

                        if (to.params.locationGroupId) vm.group = _.find(vm.groups, (g) => g.locationGroupID == to.params.locationGroupId);
					});
				})
			);
		},
        beforeRouteUpdate(to, __, next) {
            const requests = [
                users.getUser({
                    globalUserID: to.params.globalUserId
                }),
                users.getDashboardPermissions({
                    globalUserID: to.params.globalUserId,
                    locationGroupID: to.params.locationGroupId
                }),
                users.getLocationGroups({
                    globalUserID: to.params.globalUserId,
                    locationGroupID: to.params.locationGroupId
                }),
                roles.getRoles(),
                dashboards.getDashboardPermissionTypes({
                    includeDefault: true
                })
            ];


            http.all(requests)
                .then(http.spread((user, permissions, locationGroups, rolesList, permissionTypes) => {
                    this.user = user;
                    this.permissions = permissions;
                    this.groups = locationGroups;
                    this.roles = rolesList;
                    this.dashboardPermissionTypes = permissionTypes;

                    if (to.params.locationGroupId) this.group = _.find(this.groups, (g) => g.locationGroupID == to.params.locationGroupId);

                    next();
				})
			);
		},
        props: {
            globalUserId: {
                type: String,
                required: true
            },
            locationGroupId: {
                type: String,
                required: false
            }
        },
		data() {
            return {
                permissions: [],
                dashboardPermissionTypes: [],
                groups: [],
				roles: [],
                group: null,
                selectedGroupForRemoval: null,
				user: null,
                userID: this.$route.params.id,
                canDeleteUser: false
			};
		},
		computed: {
            singleGroupMode() {
				return this.group != null;
            },
            allGroupsMode() {
				return !this.singleGroupMode;
            },
            isMe() {
                return this.$auth.tokenParsed.GlobalUserId == this.globalUserId;
            },
            isReadOnlyInAnyGroup() {
                return _.some(this.editableGroups, (g) => g.readOnly === true);
            },
            editableGroups() {
                if (!this.groups) return [];

                if (!this.isMe) return this.groups;

                return _.filter(this.groups, (g) => g.role == 2); // Group admin
            }
        },
        created() {
            this.async(users.verifyDeleteUserPermission({
                globalUserID: this.globalUserId
            })).then((allowed) => {
                this.canDeleteUser = allowed;
            });
        },
        methods: {
            getPermissionsByGroup(locationGroupID) {
                return _.filter(this.permissions, (p) => p.locationGroupID == locationGroupID);
            },
            getDashboardPermissionTypeText(id) {
                return _.find(this.dashboardPermissionTypes, (t) => t.dashboardPermissionTypeID == id).dashboardPermissionTypeText;
            },
            getLocationGroupRoleType(id) {
                return _.find(this.roles, (r) => r.locationGroupRoleTypeID == id).locationGroupRoleTypeText;
            },

            updatePermission(permission) {
                const permissions = [_.pick(permission, ['permissionID', 'globalUserID', 'dashboardID', 'dashboardPermission'])];

                return this.async(users.updateDashboardPermissions({
                    globalUserID: this.globalUserId,
                    permissions
                })).then(() => {
                    notifications.open({
						content: 'Dashboard permission updated'
					});
                });
            },
            updateGroup(group, roleItem) {
                return this.async(users.updateLocationGroup({
                    globalUserID: this.globalUserId,
                    locationGroupID: group.locationGroupID,
                    role: roleItem ? roleItem.locationGroupRoleTypeID : group.role,
                })).then(() => {
                    if (roleItem)
                        group.role = roleItem.locationGroupRoleTypeID;

                    notifications.open({
						content: `"${group.reportName}" role updated`
					});
                }).catch((ex) => {
                    if (ex?.response?.data?.serviceResult == "LocationGroupHasNoOtherAdmin")
                        notifications.open({
					    	content: `"${group.reportName}" group has no other Admin. Please assign the Admin role to another User first.`
					    });
                    else if (ex?.response?.status == 403)
                        notifications.open({
					        	content: `You do not have the required permissions to update "${group.reportName}" group role.`
					        });
                    else
                        notifications.open({
					        	content: `"${group.reportName}" group role update failed.`
					        });
                });
            },

            addGroupUser() {
                this.$nextTick(() => {
                    this.$refs.addGroupUserModal.open();
                });
            },
            refreshUserGroups() {
                this.async(users.getLocationGroups({
                    globalUserID: this.globalUserId
                })).then((locationGroups) => {
                    this.groups = locationGroups;
                });

                this.async(users.getDashboardPermissions({
                    globalUserID: this.globalUserId
                })).then((permissions) => {
                    this.permissions = permissions;
                });
            },

            removeGroupUser(group) {
                this.selectedGroupForRemoval = group;
                this.$nextTick(() => {
                    this.$refs.removeGroupUserModal.open();
                });
            },
            confirmRemoveGroupUser(group) {
                this.async(groups.removeUser({
                    globalUserID: this.globalUserId,
                    locationGroupID: group.locationGroupID
                }))
                    .then(() => {
                        this.async(this.updateCurrentUserData()).then(() => {

                            notifications.open({
							    content: `${this.user.firstName} removed from "${group.reportName}"`
                            });

                            if (this.singleGroupMode) {
                                if (this.isMe) this.$router.replace({ name: 'group-list' });
                                else this.goBack();
                            }
                            else {
                                const groupIndex = _.findIndex(this.groups, (g) => g.locationGroupID == group.locationGroupID);
                                if (groupIndex !== -1) {
                                    this.$delete(this.groups, groupIndex);
                                }

                                if (!this.groups.length) {                                    
                                    if (this.isMe) this.$router.replace({ name: 'home' });
                                    else this.goBack();
                            }
                                }
                        });
                    });
            },

            revokeGroupAdminAccess(group) {
                if (!this.isMe) return;

                this.selectedGroupForRemoval = group;
                this.$nextTick(() => {
                    this.$refs.revokeGroupAdminAccessModal.open();
                });
            },
            confirmRevokeGroupAdminAccess(group) {
                group.role = 1;

                this.async(this.updateGroup(group))
                    .then(() => {
                        this.async(this.updateCurrentUserData()).then(() => {

                            notifications.open({
							    content: `Group admin access revoked from "${group.reportName}"`
                            });
                            if (this.singleGroupMode) {
                                if (this.isMe) this.$router.replace({ name: 'group-list' });
                                else this.goBack();
                            }
                            else {
                                const groupIndex = _.findIndex(this.groups, (g) => g.locationGroupID == group.locationGroupID);
                                if (groupIndex !== -1) {
                                    this.$delete(this.groups, groupIndex);

                                    if (!this.groups.length) {
                                        if (this.isMe) this.$router.replace({ name: 'home' });
                                        else this.goBack();
                                    }
                                }
                            }
                        });
                    });
            },

			deleteUser() {
				this.$refs.deleteUserModal.open();
			},
			confirmDeleteUser() {
                users.deleteUser({
                    globalUserID: this.globalUserId
                }).then(() => {
					notifications.open({
						content: 'User deleted'
					});
					this.goBack();

					this.$refs.deleteUserModal.close();
				});
            },

            updateCurrentUserData() {
                return new Promise((resolve, reject) => {
                    if (!this.isMe) resolve();

                    http.all([
                        this.$store.dispatch(dashboardStoreActions.LOCATION_GROUPS_LOAD),
                        this.$store.dispatch(actions.USER_GROUP_ROLES_LOAD)
                    ]).then(() => {
                        resolve();
                    }).catch(() => {
                        resolve();
                    });
                });
            },

            goBack() {
				this.$router.go(-1);
			}
		}
	};
</script>


<style lang="scss">
@import '~@/assets/styles/references';


.user-group-editor { width: 100%; max-width: 600px; display: flex; flex-direction: column;
    + .user-group-editor { margin-top: gx(1); }
    .collapsible-toggle:not(.disabled) { cursor: pointer; }

    .editable-toggle { display: flex; cursor: pointer; color: color(primary); }
    .editable-toggle-text,
    .editable-toggle-icon {  }
    .editable-toggle-text { flex: 1 1 auto; font-weight: $font-weight-medium; }
    .editable-toggle-icon { flex: 0 0 auto;
        .icon { font-size: 18px; line-height: 18px; }
    }
}
.user-group-editor-header { flex: 0 0 auto; display: flex; flex-direction: row; padding: 0 gx(0.5); border: 1px solid color(black, divider); background-color: color(black, state-bg); border-radius: $border-radius;
    @include hover-focus { background-color: color(black, divider); }
}
.user-group-editor-title { flex: 1 1 auto; display: flex; flex-direction: row; justify-content: flex-start; align-items: center; padding: gx(0.5); user-select: none;
    .letter-avatar { margin-right: gx(0.5); }
    .title { margin-left: gx(0.5); font-size: 14px; font-weight: $font-weight-medium; }
}
.user-group-editor-role { flex: 0 0 auto; display: flex; padding: 0 gx(0.5); align-items: center; }

.user-group-role-display { display: flex; font-weight: $font-weight-medium; align-items: center;
    .icon { font-size: 1em; line-height: 1em; margin-right: gx(0.25); color: color(black, secondary); }
}

.user-group-editor-options,
.user-group-editor-toggle { flex: 0 0 auto; display: flex; align-items: center; padding: 0 gx(0.25) 0 gx(0.25); user-select: none; }

.user-group-editor-options { color: color(black, secondary); }
.user-group-editor-toggle { 
    .icon {  color: color(black, secondary); transition: transform $easing-duration $easing-timing; }
    &.active {
        .icon { transform: rotate(-180deg); }
    }
}

.user-group-editor-body { position: relative; flex: 1 1 auto; border-bottom: 1px solid color(black, divider); border-right: 1px solid color(black, divider); border-left: 1px solid color(black, divider); border-radius: 0 0 $border-radius $border-radius;
    &:before { content: ''; display: block; position: absolute; top: 0; left: 0; right: 0; height: 4px; @include vertical-gradient(color(black, divider), rgba(0,0,0,0)); }
    select.control:not([size]):not([multiple]) { width: 286px; height: 28px; }
}

.dashboard-permission-editor { display: flex; align-items: center;
    .icon { margin-right: gx(0.25); font-size: 1em; }
}
</style>
